import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { AddTransferRateDto } from 'common/api/dto/rates.dto';
import {
  useAddTransferRateMutation,
  useGetRangeLabelsQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { Checkbox } from 'common/components/Checkbox/Checkbox';
import FormPrompt from 'common/components/FormPrompt';
import {
  WizardDropdown,
  WizardDropdownOption,
} from 'common/components/WizardControls/WizardDropdown';
import { HaulLabel } from 'common/models/harvestData/hauler';
import * as notifier from 'common/services/notification';
import { orange } from 'common/styles/colors';
import { FC, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { addTransferRateSchema } from 'utils/schemas/settings';
import {
  AddSettingBtn,
  AddSettingForm,
  dropdownHeightStyling,
} from '../../styles';
import { RouteInput, TrsfRateInputWrapper } from './styles';

export type TransferRoute = {
  route: string | null;
  chargeTarps: boolean;
  range: WizardDropdownOption<HaulLabel> | null;
};

/** The form to add a new transfer route. */
export const AddTransferRoute: FC = () => {
  const [showForm, setShowForm] = useState(false);
  const {
    data: labels,
    isLoading,
    isFetching,
    error,
  } = useGetRangeLabelsQuery();
  const [addTransfer, { isLoading: isSubmitting }] =
    useAddTransferRateMutation();

  const rangeOptions: WizardDropdownOption<HaulLabel>[] = useMemo(
    () =>
      labels?.map(label => ({
        label: `${label.miles} miles`,
        value: label,
      })) || [],
    [labels],
  );

  const addMethods = useForm<TransferRoute>({
    defaultValues: {
      route: null,
      chargeTarps: false,
      range: null,
    },
    resolver: yupResolver(addTransferRateSchema),
    mode: 'all',
  });
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = addMethods;

  const closeForm = () => {
    reset();
    setShowForm(false);
  };

  const onAddSubmit = async (data: TransferRoute): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    const { range, route, chargeTarps } = data;
    const dto = {
      route,
      chargeTarps,
      rangeId: range?.value.id,
    } as AddTransferRateDto;

    try {
      await addTransfer(dto).unwrap();
      notifier.showSuccessMessage('Transfer route added.');
      closeForm();
    } catch (e) {
      handleError(e, 'Unable to add new transfer route.');
    }
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch range labels.');
    }
  }, [error]);

  return showForm ? (
    <FormProvider<TransferRoute> {...addMethods}>
      <AddSettingForm onSubmit={handleSubmit(onAddSubmit)}>
        <TrsfRateInputWrapper>
          <div>
            <RouteInput
              {...register('route')}
              placeholder='Route...'
              $isError={!!errors.route}
            />
            <Form.Check.Input hidden isInvalid={!!errors.route} />
            {errors.route && (
              <Form.Control.Feedback type='invalid'>
                {errors.route.message}
              </Form.Control.Feedback>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span>Charge tarps?</span>
            <Checkbox
              formProps={{ name: 'chargeTarps' }}
              isDisabled={isSubmitting}
            />
          </div>
          <WizardDropdown<HaulLabel>
            name='range'
            placeholder='Range...'
            formControl={control}
            options={rangeOptions}
            isLoading={isLoading || isFetching}
            maxWidth='150px'
            styles={dropdownHeightStyling}
            isDisabled={isSubmitting}
          />
        </TrsfRateInputWrapper>
        <button type='submit' className='add-btn' disabled={isSubmitting}>
          Add New Route
        </button>
        <button
          type='button'
          className='cancel-btn'
          onClick={closeForm}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </AddSettingForm>
      <FormPrompt isDirty={isDirty} isSubmitting={isSubmitting} />
    </FormProvider>
  ) : (
    <AddSettingBtn type='button' onClick={() => setShowForm(true)}>
      <AddCircleOutlineOutlinedIcon htmlColor={orange} />
      Add New Route
    </AddSettingBtn>
  );
};
